/**
 * Generic primary-process configurator — the single configure→price→add
 * surface for every process.
 *
 * Mounted by ``QuoteDetail`` / ``MyProjectItemConfigure`` whenever the
 * active primary process carries a ``configurator_schema``. It renders:
 *
 *   - a material picker (catalog materials tagged for this process_type),
 *   - the process's schema-driven custom inputs via ``DynamicConfigurator``,
 *   - a quantity field,
 *   - a live price preview via ``usePriceLine`` (dispatched through the
 *     process's PricingStrategy — generic schema-driven engine, DB formula),
 *   - an add-to-cart button.
 *
 * Chain operations (finish-after-cut) attach to the persisted item and
 * are handled by the cart-operations endpoints, not duplicated here.
 *
 * The LineInput sent to ``/pricing/line`` carries the full analyzer
 * snapshot (the generic geometry channel)
 * so any pricing strategy can consume its own facts. Custom
 * schema fields ride along in ``extra_config`` for the formula to use.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useAddCartLine,
  useDeleteCartItem,
  useSavePartConfiguration,
  useUpdateCartLine,
} from "@/api/queries/cart";
import { usePriceLine } from "@/api/queries/pricing";
import { useAuthMe } from "@/api/queries/auth";
import { useMaxQtyPerLine, useTenantBranding } from "@/api/queries/branding";
import { useBusinessInfo } from "@/api/queries/businessInfo";
import { storefrontVatDisplay } from "@/lib/vatDisplay";
import { CartLimitWarning } from "@/components/cart/CartLimitWarning";
import { PriceBreakdown } from "@/components/PriceBreakdown";
import { QuantityTierChips } from "@/components/QuantityTierChips";
import { UnconfirmedItemGuard } from "@/components/UnconfirmedItemGuard";
import { WarningsPanel } from "@/components/WarningsPanel";
import { ManufacturabilityRepairCta } from "@/components/dxf/ManufacturabilityRepairCta";
import { QuoteStopActions } from "@/components/dxf/QuoteStopActions";
import {
  findingIsRepairable,
  useQuoteManufacturability,
  useQuoteValidity,
  useSplitQuoteParts,
} from "@/api/queries/quotes";
import { ApiError } from "@/lib/api";
import { isBlockingSeverity } from "@/lib/manufacturabilitySeverity";
import { resolveBoundColors } from "@/lib/boundColor";
import { formatCartLineError } from "@/lib/cartErrors";

import {
  OperationPathSection,
  type OperationPathPersistence,
} from "./OperationPathSection";
import { ConfiguratorEmptyState } from "./ConfiguratorEmptyState";
import { CustomerConfiguratorForm, fieldList } from "./CustomerConfiguratorForm";
import { useCustomerConfiguratorState } from "./useCustomerConfiguratorState";

import type {
  AddLineRequest,
  AnalysisSnapshot,
  CartLine,
  Catalog,
  PathOperationInput,
} from "@/api/types";

interface PersistenceHooks {
  /** KAN-1659 — kam řádek padá. ``"quote"`` = admin skládá nabídku: tlačítko
   *  „Přidat do nabídky" a bez zákaznické věty o nezávazném uložení. Chybí =
   *  košík zákazníka, jako dosud. */
  target?: "quote";
  addLine: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    mutate: (
      variables: { itemSlug: string; line: AddLineRequest },
      options?: { onSuccess?: () => void },
    ) => void;
  };
  updateLine: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    mutate: (
      variables: { lineId: number; payload: AddLineRequest },
      options?: { onSuccess?: () => void },
    ) => void;
  };
}

interface Props {
  catalog: Catalog;
  analysis: AnalysisSnapshot;
  lang: string;
  itemSlug: string;
  onAdded: () => void;
  editLine?: CartLine;
  /** The active primary process_type (e.g. ``"waterjet"``). */
  processType: string;
  /** ``Process.configurator_schema`` — drives the custom input fields. */
  schema?: Record<string, unknown> | null;
  /** Presigned URL to the module's out_viz view-spec (``quote.view_spec_url``),
   *  forwarded to a ``kind:"visualization"`` slot in the schema. */
  viewSpecUrl?: string | null;
  /** Reports the live ``fieldKey → chosen-option hex`` map up so a SIBLING
   *  preview pane (e.g. ``QuoteDetail``'s ``InteractiveViewer``, which is not a
   *  child of this form) can tint a bound ``model3d`` block to the customer's pick.
   *  The in-form visualization slot tints itself; this is only for the split
   *  layout where the viewer lives outside the configurator. */
  onBoundColorsChange?: (colors: Record<string, string>) => void;
  /** Customer-editable input keys for the item's graph NODE, straight off the BE
   *  (``QuoteOut.public_input_dimension_keys`` / the project operation's copy).
   *  The cart gate enforces this per node while the schema is per process, so a
   *  field the node locks must not be offered — changing it fails with
   *  ``public_input_locked``. Omitted / empty = no narrowing. */
  publicInputDimensionKeys?: string[] | null;
  /** Public slug of the QUOTE this configurator belongs to, when there is one.
   *
   *  Only the quote page has it. ``itemSlug`` is deliberately NOT a substitute:
   *  on the project page it is a project-item id and in the admin builder an
   *  order-item slug, so feeding either to ``/quotes/{slug}/manufacturability``
   *  just 404s on every material change — and a check that always fails silently
   *  is worse than no check, because the card would look like it is guarding when
   *  it isn't. Absent ⇒ the probe is not run at all. */
  quoteSlug?: string;
  /** KAN-1345 — the quote's ``auto_repair_available``: a placed repair node
   *  accepts this file. Without it the auto-repair offer has nothing to run,
   *  so it is not shown whatever the warnings say. KAN-1530: it gates the
   *  stopped quote's „Opravit soubor" exit too, which read it from nowhere. */
  autoRepairAvailable?: boolean;
  /** KAN-2068 — the item already runs on the repaired copy: the non-blocking
   *  offer has been used (its warnings are the repaired file's own). */
  repairInUse?: boolean;
  /** KAN-2081 — the original upload's preview + file name for the stopped
   *  quote's before / after review of the repair. */
  originalPreviewUrl?: string | null;
  fileName?: string;
  /** Rendered inside the multi-file batch flow — only the submit label differs
   *  ("save and back to the batch" instead of "add to cart"), because the user
   *  is working through a list rather than finishing a single part. */
  inBatch?: boolean;
  /** KAN-414 — položka je nahraná, ale zákazník ji ještě NEPOTVRDIL do
   *  košíku. Zapíná modál při odchodu ze stránky (``UnconfirmedItemGuard``).
   *
   *  Jen košíková plocha: na projektové ani admin ploše odkladiště
   *  neexistuje, tam položka do „košíku" nikdy nemíří. */
  isStaged?: boolean;
  /** KAN-1652 — konfigurace uložená na dílu (``config_snapshot`` primární
   *  operace). Seeduje formulář, ať sdílený odkaz otevře tentýž výběr. */
  storedConfig?: Record<string, unknown> | null;
  persistence?: PersistenceHooks & OperationPathPersistence;
  /** KAN-1703 — zákazník změnil interaktivní vstup (otočil díl) a analýza se
   *  přepočítává. Zobrazená cena do té doby neplatí: karta ukáže
   *  „Přepočítávám…" a do košíku nic neodejde. */
  reanalyzing?: boolean;
  /** KAN-1711 — keys of the configuration the part's analysis reads
   *  (``QuoteOperation.analysis_config_keys``). A change in one of them makes
   *  the analysis — and so the live price — stale until the part is
   *  re-measured. Absent = the analysis reads no configuration. */
  analysisConfigKeys?: string[] | null;
  /** KAN-1711 — re-measure the part under its SAVED configuration. Called
   *  after a pick the analysis reads has been saved on the part (the reanalysis
   *  reads the stored configuration, not this form's state). */
  onAnalysisInputsSaved?: () => Promise<void>;
}


/* ``dimsFromAnalysis`` / ``defaultsFromFields`` / ``seedCustomerConfiguratorValues``
   moved to ``useCustomerConfiguratorState`` — re-exported here so the surfaces
   that already import them from this module keep one import path. */
export {
  defaultsFromFields,
  seedCustomerConfiguratorValues,
} from "./useCustomerConfiguratorState";

/**
 * Form state seeded from a saved line being re-edited.
 *
 * ``config_snapshot`` carries every dimension TWICE: flattened to top-level keys
 * AND again under a nested ``dimensions`` dict — the BE stamps both so that
 * name-based readers and the canonical dict stay in sync. Spreading the snapshot
 * wholesale therefore dragged that reserved key into the form state, and because
 * ``extraConfig`` strips only ``material_code``/``quantity``/``customer_note`` it
 * rode straight back out on save as ``dimensions.dimensions`` — a dict-valued
 * "dimension" landing in ``OrderLine.dimensions_json``. ``dimensions`` is not in
 * the BE's reserved set, so nothing downstream strips it: the line's variant
 * identity (and with it the axis key and the price) silently changes on every
 * re-edit.
 *
 * Dimension values are therefore taken from ``editLine.dimensions`` — the
 * canonical dict — rather than from its flattened mirror in the snapshot.
 */
function seedFromSavedLine(editLine: CartLine | undefined): Record<string, unknown> {
  if (!editLine) return {};
  const snapshot = { ...(editLine.config_snapshot ?? {}) } as Record<string, unknown>;
  delete snapshot.dimensions;
  return { ...snapshot, ...(editLine.dimensions ?? {}) };
}

/**
 * KAN-1652 — formulář z konfigurace uložené na dílu. Tatáž past jako
 * u ``seedFromSavedLine``: rezervovaný klíč ``dimensions`` se do stavu
 * formuláře nesmí dostat.
 */
function seedFromStoredConfig(
  stored: Record<string, unknown>,
): Record<string, unknown> {
  const { dimensions, ...rest } = stored;
  const nested =
    dimensions && typeof dimensions === "object" && !Array.isArray(dimensions)
      ? (dimensions as Record<string, unknown>)
      : {};
  return { ...rest, ...nested };
}

export function DynamicPrimaryConfigurator({
  catalog,
  analysis,
  lang,
  itemSlug,
  onAdded,
  editLine,
  processType,
  schema,
  viewSpecUrl,
  onBoundColorsChange,
  publicInputDimensionKeys,
  quoteSlug,
  autoRepairAvailable = false,
  repairInUse = false,
  originalPreviewUrl = null,
  fileName,
  inBatch = false,
  isStaged = false,
  storedConfig = null,
  persistence,
  reanalyzing = false,
  analysisConfigKeys = null,
  onAnalysisInputsSaved,
}: Props): JSX.Element {
  const { t } = useTranslation("quote");
  // KAN-1432 — obchod pro koncové zákazníky ukazuje konečnou cenu; sazba je
  // náhled první aktivní země dopravy, táž jako v souhrnu košíku.
  const vat = storefrontVatDisplay(useBusinessInfo().data);

  // Seeding, snapping, and the reserved-key split live in the shared hook so
  // the onboarding price page's sample parts start on exactly the values a
  // buyer's form starts on — two implementations of these rules are two prices.
  const {
    value: custom,
    setValue: setCustom,
    materials,
    materialCode,
    quantity,
    extraConfig,
    customerNote,
    axisKey,
    axisUnit,
    axisUnresolved,
    tunables,
    tunablesSeeding,
    noMachineFits,
  } = useCustomerConfiguratorState({
    schema,
    catalog,
    processType,
    analysis,
    initialValue: editLine
      ? {
          ...seedFromSavedLine(editLine),
          material_code: editLine.material_code,
          quantity: editLine.quantity,
          // The per-part note is a dedicated column, not part of
          // config_snapshot — seed it back when re-editing a saved line.
          ...(editLine.customer_note
            ? { customer_note: editLine.customer_note }
            : {}),
        }
      : storedConfig
        ? seedFromStoredConfig(storedConfig)
        : undefined,
  });

  // THE line's variant identity — every schema field (the declared axis
  // included) rides this canonical ``dimensions`` channel like any other
  // admin-defined dimension; the component special-cases no key. One source
  // for both the price preview and the cart write, so the previewed price
  // can't drift from the persisted one.
  const lineDimensions = extraConfig;

  // Live ``fieldKey → chosen-option hex`` so a sibling preview pane can tint a
  // bound ``model3d`` block to the customer's pick (this form's own visualization
  // slot tints itself). Reported up whenever the resolved colours change.
  const boundColors = useMemo(
    () => resolveBoundColors(schema, catalog, materialCode, custom),
    [schema, catalog, materialCode, custom],
  );
  useEffect(() => {
    onBoundColorsChange?.(boundColors);
  }, [boundColors, onBoundColorsChange]);

  // ``onAdded`` is wired ONLY per call (see ``onAdd`` below), never here. Two
  // reasons, both load-bearing:
  //
  //  - handing it to the hook AND to the call fires it twice on the cart path,
  //    while the ``persistence`` adapter fires it once — the same click behaved
  //    differently depending on which surface mounted the configurator;
  //  - ``useAddCartLine`` spreads ``...options`` AFTER its own ``onSuccess``,
  //    so a caller-supplied one REPLACED the cart-query invalidation. Adding a
  //    line therefore left the cart badge and contents stale until the next
  //    refetch. (``useUpdateCartLine`` composes instead of replacing, hence the
  //    asymmetry.)
  const cartAddLine = useAddCartLine();
  const cartUpdateLine = useUpdateCartLine();
  // „Zahodit" z modálu při odchodu (KAN-414). Nepotvrzená položka
  // v košíku není, ale endpoint je týž — ``delete_item`` od KAN-414
  // obsluhuje obě fáze.
  const deleteItem = useDeleteCartItem();
  const addLine = persistence?.addLine ?? cartAddLine;
  const updateLine = persistence?.updateLine ?? cartUpdateLine;

  // Gated on ``axisUnresolved`` for the same reason the submit button below
  // is: until the axis is seeded, ``lineDimensions`` has no value for it. The
  // price call is not lenient the way the cart write is — it answers 422
  // ``pricing_unavailable`` for the axis-less variant — so an ungated query
  // fired one 422 on every mount and the card flashed "cena se nepodařila
  // spočítat" until the seeded re-fetch replaced it. Harmless-looking, except
  // it makes a genuinely broken price (a wrong CORS origin, say) indis-
  // tinguishable from the boot flash. Both escape hatches out of
  // ``axisUnresolved`` are "nothing to know" rather than "not known yet", so
  // this cannot deadlock the price any more than it deadlocks submit.
  // Cesta za primárkou. Posílá se do ``/pricing/line``, které od
  // KAN-121 nacení CELOU položku — klient tedy žádnou cenu nesčítá.
  const [pathOperations, setPathOperations] = useState<PathOperationInput[]>(
    [],
  );
  // KAN-1642: ani před dosazením výchozích voleb dílny — jinak by každé
  // dosazení pustilo další výpočet a cena by přišla až po posledním z nich.
  // KAN-1652 — výběr u dílu v odkladišti se průběžně ukládá na díl, takže
  // „Sdílet" i „Poslat poptávku" jen odkazují na díl a příjemce vidí
  // tentýž výběr za tutéž cenu. Neuložený výběr (cizí příjemce odkazu,
  // výpadek) se nehlásí: odkaz pak otevře poslední uložený stav.
  const savePartConfig = useSavePartConfiguration();
  const savePartConfigRef = useRef(savePartConfig.mutate);
  savePartConfigRef.current = savePartConfig.mutate;
  const configPayloadKey =
    isStaged && quoteSlug && materialCode && !axisUnresolved
      ? JSON.stringify({ materialCode, quantity, lineDimensions })
      : null;

  // KAN-1711 — what the analysis reads out of this selection. The saved
  // configuration is flat (``dimensions`` merge into ``config_snapshot``), so
  // the keys are looked up in the same flat shape. ``null`` = nothing to track:
  // no such keys, or the selection is not saved on the part (only a staged
  // part is — a cart line is re-measured by the cart itself on update).
  const analysisSig = useMemo(() => {
    if (!analysisConfigKeys?.length || configPayloadKey === null || tunablesSeeding) return null;
    const flat: Record<string, unknown> = {
      ...lineDimensions,
      material_code: materialCode,
      quantity,
    };
    return JSON.stringify(analysisConfigKeys.map((key) => flat[key] ?? null));
  }, [analysisConfigKeys, configPayloadKey, tunablesSeeding, lineDimensions, materialCode, quantity]);
  // The selection the shown analysis was measured under. The first settled
  // selection is taken as measured — the part was analysed on upload.
  const [analyzedSig, setAnalyzedSig] = useState<string | null>(null);
  useEffect(() => {
    if (analyzedSig === null && analysisSig !== null) setAnalyzedSig(analysisSig);
  }, [analyzedSig, analysisSig]);
  const analysisStale =
    analysisSig !== null && analyzedSig !== null && analysisSig !== analyzedSig;
  const analysisSigRef = useRef(analysisSig);
  analysisSigRef.current = analysisSig;
  const analyzedSigRef = useRef(analyzedSig);
  analyzedSigRef.current = analyzedSig;
  const onAnalysisInputsSavedRef = useRef(onAnalysisInputsSaved);
  onAnalysisInputsSavedRef.current = onAnalysisInputsSaved;

  useEffect(() => {
    if (configPayloadKey === null || quoteSlug === undefined) return;
    const timer = window.setTimeout(() => {
      const sigAtSave = analysisSigRef.current;
      const owesReanalysis = sigAtSave !== null && sigAtSave !== analyzedSigRef.current;
      savePartConfigRef.current(
        {
          itemSlug: quoteSlug,
          payload: {
            material_code: materialCode,
            quantity,
            dimensions: lineDimensions,
          },
        },
        {
          onSettled: (_data, error) => {
            if (!owesReanalysis) return;
            const reanalyze = onAnalysisInputsSavedRef.current;
            // A failed save or reanalysis falls back to the analysis on hand
            // rather than an endless „Přepočítávám…" — the cart re-measures
            // the part on add anyway (KAN-1557).
            const settle = () => setAnalyzedSig(sigAtSave);
            if (error || !reanalyze) settle();
            else void reanalyze().finally(settle);
          },
        },
      );
    }, 600);
    return () => window.clearTimeout(timer);
    // ``configPayloadKey`` nese celý výběr — jeho změna je jediný spouštěč.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configPayloadKey]);

  const priceQuery = usePriceLine(
    materialCode && !axisUnresolved && !tunablesSeeding
      ? {
          process_type: processType,
          // Full analyzer snapshot — lets a volume/mesh process price from its
          // own analyzer outputs (volume_mm3, …), not just the DXF geometry.
          analysis,
          material_code: materialCode,
          quantity,
          is_first_line_of_item: true,
          // Mirrors the cart payload ``onAdd`` builds below, so the preview
          // prices the exact variant identity the line will persist with.
          extra_config: { ...extraConfig, dimensions: lineDimensions },
          // Cena celé položky včetně cesty — součet dělá server (KAN-121).
          path_operations: pathOperations,
          lang,
        }
      : null,
  );
  // Surface ANY price failure, not just an ApiError's stable code: a known code
  // (e.g. ``pricing_unavailable``) renders its specific message, while a 5xx /
  // network error (no ApiError) gets a generic sentinel. Non-null whenever the
  // query errored so the price card shows an error state — and the add-to-cart
  // button stays disabled — instead of an eternal "Počítám cenu…" spinner.
  const priceErrorCode = priceQuery.isError
    ? priceQuery.error instanceof ApiError
      ? priceQuery.error.code
      : "pricing_failed"
    : null;
  // KAN-744: params jdou do karty s cenou spolu s kódem — zákazník vidí
  // třídně správnou hlášku, admin téhož tenanta navíc debug řádek
  // (error_code + params), aby příčinu nemusel lovit v network tabu.
  const priceErrorParams =
    priceQuery.isError && priceQuery.error instanceof ApiError
      ? priceQuery.error.params
      : null;
  // KAN-1047 — cenová chyba už NENÍ automaticky zeď. Rozhoduje BE
  // příznakem ``can_add_unpriced`` v tom 422: chybí-li tenantovi sazba
  // nebo cenový řádek, zákazník díl do košíku dostane bez ceny a nechá
  // si ho nacenit ručně. Chyba, kterou si spraví sám, blokuje dál — tam
  // je správná odpověď oprava, ne ruční nabídka.
  const canAddUnpriced =
    Boolean(priceErrorCode) && priceErrorParams?.can_add_unpriced === true;
  const authMe = useAuthMe();
  const tenantBranding = useTenantBranding();
  // KAN-1903 — per-line cap, the same setting the cart enforces.
  const maxQuantity = useMaxQtyPerLine();
  // Týž gate jako ``useEditModeAccess``: admin membership v PRÁVĚ
  // resolvovaném tenantu (slug z brandingu), nebo platform admin.
  const isTenantAdmin =
    authMe.data?.is_platform_admin === true ||
    (authMe.data?.org_memberships ?? []).some(
      (m) => m.slug === tenantBranding.data?.slug && m.role === "admin",
    );
  // Neustálený formulář = cena na obrazovce už neplatí (KAN-1642): rychlé
  // klikání nesmí nechat viset cenu předchozí volby, ani ji pustit do košíku.
  const isLoadingPrice =
    reanalyzing ||
    analysisStale ||
    priceQuery.isSettling ||
    (priceQuery.isFetching && !priceQuery.data);

  // Per-process manufacturability probe, run as soon as the quote exists (a
  // limit on the file itself blocks before any pick, KAN-1298) and re-run
  // whenever the pick changes. A
  // BLOCKING warning (typically a hole the process cannot cut at this variant)
  // must stop the order: the BE gate is best-effort — it short-circuits to a
  // no-op when the item carries no analysis snapshot or the module declares no
  // validator — so where it does not hold, this is the only thing standing
  // between the customer and an unmakeable part. Where it does hold, blocking
  // here still turns a blind 422 into an explanation plus a way out.
  const manufQuery = useQuoteManufacturability(
    quoteSlug ?? null,
    materialCode || null,
    lineDimensions,
    { enabled: Boolean(quoteSlug) },
  );
  // Volume-discount brackets for the picked material. Clicking one jumps the
  // quantity to that bracket's lower bound — the cheapest way into the next
  // price step, which the customer otherwise has to find by trial and error.
  const quoteValidity = useQuoteValidity();
  // Bez načtené hodnoty se číslo neukazuje — pevná čtrnáctka by u obchodu
  // s jinou platností tvrdila jiné číslo než košík (KAN-1788).
  const validityDays = quoteValidity.data?.validity_days ?? null;
  const manufBlocking = manufQuery.data?.blocking === true;
  // KAN-2070 — once the customer took the risk, the findings they accepted
  // read as warnings, not as a stop.
  const riskAccepted = manufQuery.data?.risk_accepted === true;
  const manufWarnings = useMemo(
    () =>
      (manufQuery.data?.warnings ?? []).map((w) =>
        riskAccepted && w.params?.severity === "customer_decides"
          ? { ...w, params: { ...w.params, severity: "warn" } }
          : w,
      ),
    [manufQuery.data?.warnings, riskAccepted],
  );
  // The uploaded file's own defects (splines, duplicate lines, unknown units…).
  // Most carry no ``component_id``, so the preview overlay cannot draw them and
  // until KAN-1234 no surface of the quote page named them at all.
  const fileWarnings = analysis.warnings ?? [];
  // KAN-2072 — automatic repair only for a finding an installed app declares
  // it fixes: a stop through its ``auto_repair`` remedy, the non-blocking offer
  // through the declared list (the file's own defects included).
  const repairableFindings = manufQuery.data?.repairable_findings ?? [];
  const stopRepairOffered = manufWarnings.some((w) =>
    (w.remedies ?? []).some((r) => r.kind === "auto_repair"),
  );
  const warningRepairOffered = [...manufWarnings, ...fileWarnings].some((w) =>
    findingIsRepairable(w, repairableFindings),
  );
  // KAN-1639 — „Rozdělit na díly": the parts land as a batch, where each has
  // its own configurator; the multi-part quote itself is gone afterwards, so
  // the page leaves it. A plain navigation — the configurator also renders
  // outside a router (admin builder, previews), where ``useNavigate`` throws.
  const splitParts = useSplitQuoteParts({
    onSuccess: (res) => window.location.assign(`/${lang}/quote-batch/${res.public_id}`),
  });

  // Jeden tvar payloadu pro obě cesty do košíku — tlačítko i modál při
  // odchodu (KAN-414). Kdyby se rozešly, „Přidat do košíku" v modálu by
  // uložilo jinou konfiguraci než totéž tlačítko o řádek níž.
  const buildLine = () => ({
    material_code: materialCode,
    quantity,
    dimensions: { ...lineDimensions },
    extra_config: { ...extraConfig },
    // KAN-1047 — „chci to i tak". Posílá se JEN když BE u téhle chyby
    // sám řekl, že to jde (``can_add_unpriced``); posílat ho vždy by
    // znamenalo držet si na FE druhou kopii seznamu chybových kódů,
    // která se s BE rozejde při prvním novém kódu.
    ...(canAddUnpriced ? { allow_unpriced: true } : {}),
    // Only sent when the schema declares a ``customer_note`` field (the
    // key is absent from ``custom`` otherwise). Empty string → null so
    // clearing the note on re-edit clears the stored value.
    ...(customerNote !== undefined
      ? { customer_note: customerNote.trim() || null }
      : {}),
  });

  // Vypínač hlídání odchodu, sdílený s ``UnconfirmedItemGuard``. Bez něj
  // by blocker chytil i tu navigaci, kterou spouští ÚSPĚŠNÉ přidání do
  // košíku — zákazník klikne „Přidat do košíku" a dostane modál
  // s otázkou, jestli to chce přidat do košíku. Ref, ne stav: ``onAdded``
  // naviguje hned v ``onSuccess``, tedy dřív, než by se ``setState``
  // stihl promítnout.
  const cartGuardDisarmed = useRef(false);

  const onAdd = () => {
    if (!materialCode) return;
    const line = buildLine();
    if (editLine) {
      updateLine.mutate({ lineId: editLine.id, payload: line }, { onSuccess: onAdded });
      return;
    }
    addLine.mutate(
      { itemSlug, line },
      {
        onSuccess: () => {
          cartGuardDisarmed.current = true;
          onAdded();
        },
      },
    );
  };

  // Odchod z konfigurátoru s nepotvrzenou položkou (KAN-414). Potvrzení
  // z modálu jde ZÁMĚRNĚ mimo ``onAdded``: ten navádí na košík, kdežto
  // tady zákazník už někam mířil a modál mu tu cestu jen pozdržel.
  const submitBlocked =
    isLoadingPrice ||
    (Boolean(priceErrorCode) && !canAddUnpriced) ||
    manufBlocking ||
    axisUnresolved ||
    noMachineFits;
  const confirmFromGuard = async () => {
    await cartAddLine.mutateAsync({ itemSlug, line: buildLine() });
  };
  const discardFromGuard = async () => {
    await deleteItem.mutateAsync(itemSlug);
  };
  // Vykresluje se ve VŠECH návratových větvích, i v těch degenerovaných
  // níž (proces bez vstupů / bez materiálů). Právě tam je nepotvrzená
  // položka nejvíc v ohrožení: potvrdit ji nejde, takže by bez modálu
  // odešla úplně potichu — modál v takové chvíli nabídne jen „Zahodit".
  const unconfirmedGuard =
    isStaged && !editLine ? (
      <UnconfirmedItemGuard
        disarmedRef={cartGuardDisarmed}
        canConfirm={Boolean(materialCode) && !submitBlocked}
        onConfirm={confirmFromGuard}
        onDiscard={discardFromGuard}
      />
    ) : null;

  // A schema that declares no fields renders no inputs whatsoever — the material
  // picker and the quantity box are themselves schema fields, owned by
  // ``CustomerConfiguratorForm``. Without this guard the card shows an empty box,
  // silently seeds the first material, prices it, and offers an ENABLED
  // "add to cart" that the BE then rejects with ``line_dimensions_missing``:
  // a fabricated price for a configuration the customer never made.
  //
  // The test is the FIELD LIST, deliberately not ``hasDynamicSchema`` — a process
  // created through the admin UI is stored as
  // ``{"type":"object","properties":{},"required":[]}``, which is non-empty and
  // yet field-less. The submit button is not rendered at all rather than
  // disabled: there is nothing to submit, and a greyed-out button invites
  // clicking and waiting.
  //
  // This is about a schema that EXISTS but is empty. A process whose module could
  // not be resolved at all is a different state and belongs to the mount site,
  // which is the only place that knows the difference.
  if (fieldList(schema).length === 0) {
    return (
      <>
      {unconfirmedGuard}
      <ConfiguratorEmptyState
        kind="noFields"
        lang={lang}
        quoteSlug={quoteSlug}
        isTenantAdmin={isTenantAdmin}
        onBeforeInquiry={() => {
          cartGuardDisarmed.current = true;
        }}
        testId="dynamic-primary-no-fields"
      />
      </>
    );
  }

  if (materials.length === 0) {
    return (
      <>
      {unconfirmedGuard}
      <ConfiguratorEmptyState
        kind="noMaterials"
        lang={lang}
        quoteSlug={quoteSlug}
        isTenantAdmin={isTenantAdmin}
        onBeforeInquiry={() => {
          cartGuardDisarmed.current = true;
        }}
        testId="dynamic-primary-no-materials"
      />
      </>
    );
  }

  return (
    <div className="space-y-4" data-testid="dynamic-primary-configure">
      {unconfirmedGuard}
      <div className="space-y-3 rounded-md border border-border bg-surface p-4">
        <CustomerConfiguratorForm
          schema={schema}
          catalog={catalog}
          analysis={analysis}
          processType={processType}
          tunables={tunables}
          value={custom}
          onChange={setCustom}
          lang={lang}
          viewSpecUrl={viewSpecUrl}
          axisKey={axisKey}
          axisUnit={axisUnit}
          allowedKeys={publicInputDimensionKeys}
          maxQuantity={maxQuantity}
          // Srovnání voleb až po hlavní ceně (KAN-1642/1786): i dokud je hlavní
          // dotaz vypnutý (osa, výchozí hodnoty), cena „ještě nedorazila“.
          deferLivePricing={
            isLoadingPrice ||
            axisUnresolved ||
            tunablesSeeding ||
            (!priceQuery.data && !priceQuery.isError)
          }
        />
      </div>

      <OperationPathSection
        itemSlug={itemSlug}
        primaryProcessType={processType}
        cascadeConfig={{
          material_code: materialCode,
          quantity,
          dimensions: { ...lineDimensions },
        }}
        lang={lang}
        onPathChange={setPathOperations}
        persistence={persistence}
      />

      <QuantityTierChips
        quantity={quantity}
        materialCode={materialCode}
        onPick={(qty) => setCustom((prev) => ({ ...prev, quantity: qty }))}
      />

      {/* KAN-1234 — what stands between this file and its price sits AT the
          price: the file's own defects (analyzer) and the verdict for the
          current pick (manufacturability). Only on the quote page — the other
          surfaces render the file's warnings themselves, and there is no quote
          to repair. A stop offers its ways out right under the reason; a defect
          that does not stop the price offers the repair. */}
      {quoteSlug !== undefined && fileWarnings.length + manufWarnings.length > 0 && (
        <WarningsPanel
          warnings={fileWarnings}
          status={analysis.status}
          manufacturabilityWarnings={manufWarnings}
          blocking={manufBlocking}
          onSplitParts={() => splitParts.mutate({ quoteSlug, lang })}
          splitPending={splitParts.isPending}
          splitError={splitParts.isError}
        />
      )}
      {quoteSlug !== undefined && manufBlocking && (
        <QuoteStopActions
          quoteSlug={quoteSlug}
          lang={lang}
          // KAN-1530 — the same gate the non-blocking offer below already
          // had: without a placed node the repair has nothing to run.
          repairAvailable={autoRepairAvailable && stopRepairOffered}
          dimensions={lineDimensions}
          originalPreviewUrl={originalPreviewUrl}
          fileName={fileName}
          onBeforeInquiry={() => {
            cartGuardDisarmed.current = true;
          }}
          // KAN-2073 — týž výběr jako blokující sekce varování: nález se
          // zastavující severitou, bez ní podle celkového verdiktu.
          findings={manufWarnings.filter((w) =>
            typeof w.params?.severity === "string"
              ? isBlockingSeverity(w.params.severity)
              : manufBlocking,
          )}
          ownRisk={{
            warnings: manufWarnings,
            material: materialCode || null,
            dimensions: lineDimensions,
          }}
        />
      )}
      {quoteSlug !== undefined && riskAccepted && (
        <p
          className="rounded-md border border-warn-border bg-warn-bg px-3 py-2 text-sm text-ink"
          data-testid="quote-own-risk-accepted"
        >
          {t("ownRisk.accepted")}
        </p>
      )}
      {quoteSlug !== undefined &&
        !manufBlocking &&
        fileWarnings.length + manufWarnings.length > 0 && (
          <ManufacturabilityRepairCta
            quoteSlug={quoteSlug}
            hasFixableWarnings={autoRepairAvailable && warningRepairOffered}
            alreadyApplied={repairInUse}
            dimensions={lineDimensions}
          />
        )}

      <PriceBreakdown
        breakdown={priceQuery.data}
        isLoading={isLoadingPrice}
        isRecalculating={reanalyzing || analysisStale}
        errorCode={priceErrorCode}
        errorParams={priceErrorParams}
        canAddUnpriced={canAddUnpriced}
        isTenantAdmin={isTenantAdmin}
        lang={lang}
        locale={lang === "cs" ? "cs-CZ" : "en-US"}
        weightKg={priceQuery.data?.weight_kg ?? null}
        vat={vat}
        blockedByManufacturability={manufBlocking}
      />

      {/* KAN-1827 — horní limity objednávky: verdikt serveru z košíku. */}
      {persistence?.target !== "quote" && (
        <CartLimitWarning locale={lang === "cs" ? "cs-CZ" : "en-US"} />
      )}

      {noMachineFits && (
        <p
          className="mb-2 rounded-md border border-warn bg-warn-bg px-3 py-2 text-[12.5px] text-ink"
          data-testid="dynamic-primary-no-machine-fits"
        >
          {t("price.noMachineFits", {
            defaultValue:
              "Tuhle kombinaci (materiál a parametry) žádný stroj dílny nezpracuje. Zkuste jinou hodnotu nebo materiál.",
          })}
        </p>
      )}

      <button
        type="button"
        onClick={onAdd}
        disabled={addLine.isPending || updateLine.isPending || submitBlocked}
        data-testid="dynamic-primary-add-to-cart"
        className="w-full rounded-md bg-accent px-4 py-2 text-sm font-semibold text-bg transition hover:bg-accent-ink disabled:opacity-60"
      >
        {addLine.isPending || updateLine.isPending
          ? t("price.adding", { defaultValue: "Přidávám…" })
          : editLine
            ? t("actions.saveChanges", { defaultValue: "Uložit změny" })
            : inBatch
              ? t("actions.saveAndBackToBatch", {
                  defaultValue: "Uložit a zpět do dávky",
                })
              : persistence?.target === "quote"
                ? t("price.addToQuote", { defaultValue: "Přidat do nabídky" })
              : canAddUnpriced
                ? // KAN-1047 — jiný popisek, protože je to jiný slib.
                  // „Přidat do košíku" by tvrdilo, že se položka chová
                  // jako ostatní; tahle čeká na ruční nacenění a zákazník
                  // to musí vědět DŘÍV, než klikne.
                  t("price.addToCartUnpriced", {
                    defaultValue: "Přidat a vyžádat cenu",
                  })
                : t("price.addToCart", { defaultValue: "Přidat do košíku" })}
      </button>

      {persistence?.target !== "quote" && validityDays !== null && (
        <p className="pt-2 text-center text-[11px] leading-relaxed text-ink-3">
          {t("footer.validity", { count: validityDays })}
        </p>
      )}

      {/* Report the mutation this click actually used. Edit mode goes through
          ``updateLine``, whose failure used to be reported nowhere at all — the
          customer pressed "save", nothing moved, and no message appeared. */}
      {(editLine ? updateLine.isError : addLine.isError) && (
        <p
          className="text-sm text-danger"
          role="alert"
          data-testid="dynamic-primary-add-error"
        >
          {formatCartLineError(editLine ? updateLine.error : addLine.error, t)}
        </p>
      )}
    </div>
  );
}
